import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import colors from '../../../utils/colors';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import Portal from '../../general-components/portal';
import { setMetaMenuRoute, setMetaMenuState } from '../../../stores/actions/tabs';
import { META_MENU_ROUTES, META_MENU_STATES } from '../../../stores/types/tabs';
import { setSelectedOnsiteLocationId, setSharingScreen } from '../../../stores/actions/video';
import { GreenroomState as GS } from '../../../stores/types/types';
import { CreateOnsiteLocation } from '../../../connection/video';
import { EntityType } from '../../../types/enums';
import { STATIC_ASSET_URL } from '../../../utils/content-templates';

const Button = styled.button`
	display: flex;
	padding: 16px 24px;
	height: 50px;
	justify-content: center;
	align-items: center;
	gap: 8px;
	flex: 1 0 0;
	border-radius: 100px;
	background: ${colors.blGrayscale600};

	:hover {
		background: ${colors.blGrayscale500};
	}

	&.open {
		background: ${colors.blGrayscale700};

		label {
			color: ${colors.blGrayscale300};
		}

		svg {
			path {
				fill: ${colors.blGrayscale300};
			}
		}
	}
`;

const ButtonLabel = styled.label`
	font-size: 13px;
	font-weight: 600;
	line-height: 16px;
	color: ${colors.blGrayscale200};
	cursor: pointer;
`;

const OptionsContainer = styled.div`
	position: absolute;
	bottom: 63px;
	right: 108px;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	width: 261px;
	z-index: 97;
	visibility: hidden;
	opacity: 0;
	transition: opacity 250ms;
	&.open {
		visibility: visible;
		opacity: 1;
	}
`;

const OptionButton = styled.button`
	display: flex;
	justify-content: flex-start;
	align-items: center;
	align-self: stretch;
	height: 36px;
	padding: 8px 16px;
	border-radius: 0;
`;

const OptionIconContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 16px;
	height: 16px;
	margin-right: 8px;
`;

const OptionLabel = styled.label`
	font-size: 12px;
	font-weight: 500;
	line-height: 16px;
	letter-spacing: 0.24px;
	cursor: pointer;
`;

export default function AddFeedButton() {
	const dispatch = useDispatch();

	const sharingScreen = useSelector((s: GS) => s.VideoReducer.sharingScreen);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);

	const [optionsOpen, setOptionsOpen] = useState<boolean>(false);
	const [optionClicked, setOptionClicked] = useState<boolean>(false);

	const handleClick = () => {
		setOptionsOpen(false);
		setOptionClicked(false);
		window.removeEventListener('click', handleClick);
	};

	const handleOpenOptions = () => {
		if (!optionsOpen) {
			setOptionsOpen(true);
			setTimeout(() => {
				window.addEventListener('click', handleClick);
			}, 100);
		}
	};

	const handleInviteUser = () => {
		dispatch(setMetaMenuRoute(META_MENU_ROUTES.SHARING));
		dispatch(setMetaMenuState(META_MENU_STATES.OPENING));
	};

	const handleShareScreen = () => {
		dispatch(setSharingScreen(!sharingScreen));
	};

	const handleLocation = async () => {
		setOptionClicked(true);
		if (!token || !meeting) return;
		const entity = await CreateOnsiteLocation(
			token,
			meeting.meeting,
			null,
			`${STATIC_ASSET_URL}/default-images/onsite-safety-image.png`,
			null,
		);
		if (!entity || entity.entityType !== EntityType.onsiteLocation) {
			console.error('Failed to create onsite location');
			return;
		}
		const locationId = Number(entity.entityId);
		if (locationId) {
			dispatch(setSelectedOnsiteLocationId(locationId));
			dispatch(setMetaMenuRoute(META_MENU_ROUTES.ONSITE_LOCATION));
			dispatch(setMetaMenuState(META_MENU_STATES.OPENING));
		}
	};

	useEffect(() => () => {
		window.removeEventListener('click', handleClick);
	}, []);

	return (
		<>
			<Button className={classNames(optionsOpen && 'open')} onClick={handleOpenOptions}>
				<ButtonLabel>Add</ButtonLabel>
				<Icon
					name={
						optionsOpen
							? ICONS.KEYBOARD_ARROW_DOWN
							: ICONS.KEYBOARD_ARROW_UP
					}
					color={COLORS.TEXT_GRAY_200}
					size={12}
				/>
			</Button>
			<Portal targetId="add-feed-portal">
				<OptionsContainer
					className={classNames('add-feeds-container field-group select', optionsOpen ? 'open' : '')}
					onTransitionEnd={({ propertyName, currentTarget }) => {
						if (
							propertyName === 'visibility'
							&& currentTarget
							&& !currentTarget.className.includes('open')
						) {
							setOptionClicked(false);
						}
					}}
				>
					<OptionButton onClick={handleInviteUser} className='select-option'>
						<OptionIconContainer>
							<Icon name={ICONS.SHARE} color={COLORS.WHITE} size={16} />
						</OptionIconContainer>
						<OptionLabel>Invite User</OptionLabel>
					</OptionButton>
					<OptionButton onClick={handleShareScreen} className={classNames('select-option', {'critical-option': sharingScreen})}>
						<OptionIconContainer>
							<Icon
								name={sharingScreen ? ICONS.STOP_SCREENSHARE : ICONS.SHARE_SCREEN}
								color={sharingScreen ? COLORS.SEMANTIC_RED_400 : COLORS.WHITE}
								size={16}
							/>
						</OptionIconContainer>
						<OptionLabel className={classNames(sharingScreen && 'semantic-red-400')}>
							{sharingScreen ? 'Stop Sharing Screen' : 'Share Screen'}
						</OptionLabel>
					</OptionButton>
					<OptionButton onClick={handleLocation} disabled={optionClicked} className='select-option'>
						<OptionIconContainer>
							<Icon name={ICONS.LOCATION} color={COLORS.WHITE} size={16} />
						</OptionIconContainer>
						<OptionLabel>Location</OptionLabel>
					</OptionButton>
				</OptionsContainer>
			</Portal>
		</>
	);
}
